import React, { useMemo } from 'react'
import { supplyPerMan } from '../supply'
import { C, muteColor, ui } from '../theme'

const activeMen = (a) => (a.pools || []).reduce((s, p) => s + (p.disbanded ? 0 : p.men), 0)
const wagesDay = (a) => (a.pools || []).reduce((s, p) => s + (p.disbanded ? 0 : p.men * (p.wage_per_man || 0)), 0)
const supplyDay = (a) => (a.pools || []).reduce((s, p) => s + (p.disbanded ? 0 : p.men * supplyPerMan(p)), 0)
const gp = (n) => Math.round(n).toLocaleString()

const th = { textAlign: 'right', padding: '8px 14px', fontSize: 12, fontFamily: "'Cinzel', serif", letterSpacing: 0.5, color: C.gold, borderBottom: `2px solid ${C.ruleStrong}`, whiteSpace: 'nowrap' }
const td = { textAlign: 'right', padding: '7px 14px', fontSize: 14, borderBottom: `1px solid ${C.rule}`, fontVariantNumeric: 'tabular-nums lining-nums' }
const rowLabel = { ...td, textAlign: 'left', color: C.inkSoft, fontVariantNumeric: 'normal' }

// Spreadsheet-style economic dashboard: factions as columns, KPI rows — the tracker's
// Dashboard sheet made native, including "tomorrow's gold" from the engine's dry-run preview.
export default function LedgerView({ factions = [], armies = [], projected = null, onClose }) {
  const cols = useMemo(() => factions.map(f => {
    const mine = armies.filter(a => a.faction_id === f.id)
    const wages = mine.reduce((s, a) => s + wagesDay(a), 0)
    const supply = mine.reduce((s, a) => s + supplyDay(a), 0)
    const gold = f.treasury ?? 0
    const tomorrow = projected && projected[f.id] != null ? projected[f.id] : null
    return {
      f, armies: mine.length,
      soldiers: mine.reduce((s, a) => s + activeMen(a), 0),
      wages, supply, upkeep: wages + supply,
      gold, tomorrow, delta: tomorrow == null ? null : tomorrow - gold,
    }
  }), [factions, armies, projected])

  const rows = [
    { k: 'gold', label: 'Current gold', get: c => gp(c.gold) },
    { k: 'tomorrow', label: "Tomorrow's gold", get: c => c.tomorrow == null ? '—' : gp(c.tomorrow), color: c => c.tomorrow != null && c.tomorrow < 0 ? C.crimson : undefined },
    { k: 'delta', label: 'Net / day', get: c => c.delta == null ? '—' : (c.delta >= 0 ? '+' : '') + gp(c.delta), color: c => c.delta == null ? undefined : c.delta < 0 ? C.crimson : C.green },
    { sep: true },
    { k: 'armies', label: 'Armies', get: c => c.armies },
    { k: 'soldiers', label: 'Soldiers', get: c => gp(c.soldiers) },
    { k: 'wages', label: 'Wages / day', get: c => gp(c.wages) },
    { k: 'supply', label: 'Supply / day', get: c => gp(c.supply) },
    { k: 'upkeep', label: 'Upkeep / day', get: c => gp(c.upkeep) },
  ]

  const allArmies = [...armies].sort((a, b) => (a.faction_id - b.faction_id) || a.name.localeCompare(b.name))

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 24, background: C.paper }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Ledger</h2>
        <span style={{ fontSize: 12, color: C.inkFaint }}>
          {projected ? '“Tomorrow” is a live dry-run of the next day — nothing committed.' : 'projecting tomorrow…'}
        </span>
        <button onClick={onClose} style={{ ...ui.btnGhost, marginLeft: 'auto', padding: '7px 13px' }}>
          ← Back to map
        </button>
      </div>

      <table style={{ borderCollapse: 'collapse', marginBottom: 28, minWidth: 420 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Faction</th>
            {cols.map(c => (
              <th key={c.f.id} style={th}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 11, height: 11, borderRadius: 2, background: muteColor(c.f.color) }} />{c.f.name}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => r.sep
            ? <tr key={i}><td style={{ height: 8 }} colSpan={cols.length + 1} /></tr>
            : (
              <tr key={r.k}>
                <td style={rowLabel}>{r.label}</td>
                {cols.map(c => (
                  <td key={c.f.id} style={{ ...td, color: r.color ? r.color(c) : undefined, fontWeight: r.k === 'gold' || r.k === 'tomorrow' ? 700 : 400 }}>
                    {r.get(c)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>

      <h3 style={{ fontSize: 14, opacity: 0.7, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 1 }}>By army</h3>
      <table style={{ borderCollapse: 'collapse', minWidth: 560 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Army</th>
            <th style={{ ...th, textAlign: 'left' }}>Faction</th>
            <th style={th}>Soldiers</th>
            <th style={th}>Wages/day</th>
            <th style={th}>Supply/day</th>
            <th style={th}>Carried supply</th>
            <th style={{ ...th, textAlign: 'left' }}>Order</th>
          </tr>
        </thead>
        <tbody>
          {allArmies.map(a => {
            const order = a.pendingOrder
            const orderText = !order ? '—'
              : order.order_type === 'raid' ? `raiding (${order.payload.stance})`
              : order.order_type === 'move' ? `moving (${order.payload.path.length} left)` : order.order_type
            const under = (a.pools || []).some(p => p.unsupplied_days > 0)
            return (
              <tr key={a.id}>
                <td style={{ ...td, textAlign: 'left' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: muteColor(a.faction_color) }} />{a.name}
                  </span>
                </td>
                <td style={{ ...td, textAlign: 'left', opacity: 0.7 }}>{a.faction_name}</td>
                <td style={td}>{gp(activeMen(a))}</td>
                <td style={td}>{gp(wagesDay(a))}</td>
                <td style={td}>{gp(supplyDay(a))}</td>
                <td style={{ ...td, color: under ? C.crimson : undefined }}>{gp(a.carried_supply_gp || 0)}{under ? ' ⚠' : ''}</td>
                <td style={{ ...td, textAlign: 'left', opacity: 0.8 }}>{orderText}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
